import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import classes from './ChatWidget.module.scss'
import { ChatProduct, ChatProductCampaign, ChatProductSearch } from './types'
import { IconBox, IconChevron, IconExternal, IconTag } from './icons'
import { formatChatMoney } from './chatFormatters'
import { getProductCompanyBadge } from './productCompany'
import ProductAddToBasket, { AddBlockNote } from './ProductAddToBasket'
import { stockIndicator } from '../../base/utils/dataHelper'

function stockBadge(stock?: number | null) {
  if (stock === undefined || stock === null) return null
  const inStock = stock > 0
  return (
    <span className={`${classes.orderBadge} ${inStock ? classes.orderBadgeInvoiced : classes.orderBadgePending}`}>
      {inStock ? `Stokta: ${stockIndicator(stock)}` : 'Stokta Yok'}
    </span>
  )
}

function campaignLabel(campaign: ChatProductCampaign, index: number) {
  return campaign.label || `Kampanya ${campaign.id ?? index + 1}`
}

function CampaignNotes({ campaigns }: { campaigns: ChatProductCampaign[] }) {
  if (campaigns.length === 0) return null

  return (
    <div className={classes.productCampaignNotes} aria-label='Kampanya detayları'>
      {campaigns.map((campaign, index) => {
        const label = campaignLabel(campaign, index)
        const description = campaign.description === campaign.highlight ? null : campaign.description

        return (
          <div key={`${campaign.id ?? label}-${index}`} className={classes.productCampaignNote}>
            <span className={classes.productCampaignName}>{label}</span>
            {campaign.highlight && <strong>{campaign.highlight}</strong>}
            {description && <span>{description}</span>}
          </div>
        )
      })}
    </div>
  )
}

interface CardProps {
  product: ChatProduct
  /** Tek ürün (detay senaryosu) → geniş kart, özellikler açık gelir. */
  single?: boolean
}

/** Tek ürün kartı: görsel + ad/kod/marka + stok rozeti + fiyat + özellik tablosu (katlanır). */
function ProductCard({ product, single }: CardProps) {
  const [specsOpen, setSpecsOpen] = useState(Boolean(single))
  // Görsel adresi gelse de yüklenemeyebilir (silinmiş dosya) → kutu ikonuna düş.
  const [imageBroken, setImageBroken] = useState(false)
  const hasSpecs = Boolean(product.specs && product.specs.length > 0)
  // Portalda bulunamayan ürün (ör. yalnız SAP'te olan): görsel alanı hiç çizilmez, detaya link verilmez.
  const nameOnly = Boolean(product.nameOnly)
  const showImage = Boolean(product.imageUrl) && !imageBroken
  const companyBadge = getProductCompanyBadge(product.companyId)
  const campaigns = product.campaigns ?? []
  const campaignTitle = campaigns.map(campaignLabel).join('\n')

  return (
    <div
      className={`${classes.productCard} ${single ? classes.productCardSingle : ''} ${
        nameOnly ? classes.productCardNameOnly : ''
      }`}
    >
      {!nameOnly && (
        <div className={classes.productMedia}>
          {showImage ? (
            <img src={product.imageUrl!} alt={product.name} loading='lazy' onError={() => setImageBroken(true)} />
          ) : (
            <span className={classes.productMediaFallback}>
              <IconBox />
            </span>
          )}
          {companyBadge && (
            <span className={classes.productCompanyBadge} title={companyBadge.name}>
              <img src={companyBadge.logoSrc} alt={`${companyBadge.name} şirketi`} />
            </span>
          )}
          {campaigns.length > 0 && (
            <span
              className={classes.productCampaignBadge}
              title={campaignTitle}
              aria-label={`Kampanyalı ürün: ${campaignTitle}`}
            >
              <IconTag />
              <span>{campaigns.length === 1 ? 'Kampanya' : `${campaigns.length} Kampanya`}</span>
            </span>
          )}
          {product.brand && <span className={classes.productBrandBadge}>{product.brand}</span>}
        </div>
      )}

      <div className={classes.productBody}>
        <div className={classes.productName} title={product.name}>
          {product.name}
        </div>
        <div className={classes.productMeta}>
          <span className={classes.productCode}>{product.code}</span>
          {product.category && <span className={classes.productCategory}>{product.category}</span>}
        </div>

        {product.description && <p className={classes.productDescription}>{product.description}</p>}

        <div className={classes.productStatusRow}>
          {product.statBadge && <span className={classes.productStatBadge}>{product.statBadge}</span>}
          {stockBadge(product.stock)}
          {product.price !== undefined && product.price !== null && (
            <span className={classes.productPrice}>{formatChatMoney(product.price, product.currency ?? 'TRY')}</span>
          )}
        </div>

        <CampaignNotes campaigns={campaigns} />

        {hasSpecs && (
          <>
            <button
              type='button'
              className={classes.productSpecsToggle}
              onClick={() => setSpecsOpen(v => !v)}
              aria-expanded={specsOpen}
            >
              Teknik Özellikler
              <span className={`${classes.productSpecsChevron} ${specsOpen ? classes.productSpecsChevronOpen : ''}`}>
                <IconChevron />
              </span>
            </button>
            {specsOpen && (
              <dl className={classes.productSpecs}>
                {product.specs!.map((spec, i) => (
                  <div key={i} className={classes.productSpecRow}>
                    <dt>{spec.label}</dt>
                    <dd>{spec.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </>
        )}

        {product.addable && product.productId && !nameOnly ? (
          <ProductAddToBasket product={product} />
        ) : (
          <AddBlockNote product={product} />
        )}

        {product.productId && !nameOnly ? (
          <Link
            className={classes.productDetailLink}
            to={`/product/detail/${product.productId}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            Ürünü İncele
            <IconExternal />
          </Link>
        ) : null}
      </div>
    </div>
  )
}

interface Props {
  products: ChatProduct[]
  /** Doluysa ve kart sayısı toplamdan azsa listenin BAŞINDA "Tümünü Getir" bağlantısı gösterilir (Faz-42). */
  search?: ChatProductSearch
}

/**
 * Sohbette gösterilenden fazla eşleşme varsa ("asus laptop" gibi geniş sorgular) kalanı
 * ürün listesinde yeni sekmede açar; sohbet akışı bozulmasın diye aynı sekmede gezinmez.
 */
function ShowAllLink({ search }: { search: ChatProductSearch }) {
  const remaining = search.totalCount - search.shownCount
  if (remaining <= 0) return null

  return (
    <Link
      className={classes.productShowAll}
      to={`/products?searchKey=${encodeURIComponent(search.keyword)}`}
      target='_blank'
      rel='noopener noreferrer'
    >
      Tümünü Getir ({search.totalCount} ürün)
      <IconExternal />
    </Link>
  )
}

interface ScrollState {
  canScrollLeft: boolean
  canScrollRight: boolean
}

const INITIAL_SCROLL_STATE: ScrollState = {
  canScrollLeft: false,
  canScrollRight: false,
}

const SCROLL_EDGE_TOLERANCE = 2

function ProductSlider({ products }: { products: ChatProduct[] }) {
  const sliderRef = useRef<HTMLDivElement>(null)
  const [scrollState, setScrollState] = useState<ScrollState>(INITIAL_SCROLL_STATE)

  const updateScrollState = useCallback(() => {
    const slider = sliderRef.current
    if (!slider) return

    const maxScrollLeft = slider.scrollWidth - slider.clientWidth
    const nextState = {
      canScrollLeft: slider.scrollLeft > SCROLL_EDGE_TOLERANCE,
      canScrollRight: slider.scrollLeft < maxScrollLeft - SCROLL_EDGE_TOLERANCE,
    }

    setScrollState(current =>
      current.canScrollLeft === nextState.canScrollLeft && current.canScrollRight === nextState.canScrollRight
        ? current
        : nextState
    )
  }, [])

  useEffect(() => {
    const slider = sliderRef.current
    if (!slider) return

    updateScrollState()
    slider.addEventListener('scroll', updateScrollState, { passive: true })
    window.addEventListener('resize', updateScrollState)
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScrollState)
    resizeObserver?.observe(slider)

    return () => {
      slider.removeEventListener('scroll', updateScrollState)
      window.removeEventListener('resize', updateScrollState)
      resizeObserver?.disconnect()
    }
  }, [products.length, updateScrollState])

  const scrollByCard = (direction: -1 | 1) => {
    const slider = sliderRef.current
    const firstCard = slider?.firstElementChild as HTMLElement | null
    if (!slider || !firstCard) return

    const sliderStyles = window.getComputedStyle(slider)
    const gap = Number.parseFloat(sliderStyles.columnGap || sliderStyles.gap) || 0
    slider.scrollBy({ left: direction * (firstCard.offsetWidth + gap) })
  }

  const hasOverflow = scrollState.canScrollLeft || scrollState.canScrollRight

  return (
    <div className={classes.productSliderShell}>
      <div ref={sliderRef} className={classes.productSlider}>
        {products.map((product, i) => (
          <ProductCard key={`${product.code}-${i}`} product={product} />
        ))}
      </div>

      {hasOverflow ? (
        <>
          <button
            type='button'
            className={`${classes.productSliderNav} ${classes.productSliderNavPrevious}`}
            onClick={() => scrollByCard(-1)}
            disabled={!scrollState.canScrollLeft}
            aria-label='Önceki ürünleri göster'
            title='Önceki ürünler'
          >
            <IconChevron />
          </button>
          <button
            type='button'
            className={`${classes.productSliderNav} ${classes.productSliderNavNext}`}
            onClick={() => scrollByCard(1)}
            disabled={!scrollState.canScrollRight}
            aria-label='Sonraki ürünleri göster'
            title='Sonraki ürünler'
          >
            <IconChevron />
          </button>
        </>
      ) : null}
    </div>
  )
}

/**
 * Ürün kartları: tek ürün gelirse (ürün detay/stok/fiyat) geniş dikey kart, birden çok ürün
 * gelirse (arama sonucu) yatay kaydırmalı kart şeridi — Stitch referans tasarımındaki slider gibi.
 */
export default function ProductCards({ products, search }: Props) {
  if (products.length === 0) return null

  if (products.length === 1) {
    return (
      <div className={classes.productList}>
        {/* Faz-42: bağlantı listenin başında — bayi önce tüm sonuçlara gidebildiğini görür. */}
        {search && <ShowAllLink search={search} />}
        <ProductCard product={products[0]} single />
      </div>
    )
  }

  return (
    <>
      {/* Faz-42: bağlantı listenin başında — bayi önce tüm sonuçlara gidebildiğini görür. */}
      {search && <ShowAllLink search={search} />}
      <ProductSlider products={products} />
    </>
  )
}
